<template>
  <div id="information" ref="container" class="about-section px-4">
    <!-- About the Event card -->
    <div v-if="hasFullEventDetails && aboutOnlineEvent" class="about-card">
      <h2 class="about-card__title">{{ title }}</h2>
      <div
        v-html="aboutOnlineEvent"
        class="about-card__body rich-text html-content"
      ></div>
    </div>

    <!-- FAQ items (collapsible, below the about text) -->
    <div v-if="lobbyFaqEnabled && faqsConverted.length" class="faq-list mt-4">
      <CollapsibleList ref="faqList">
        <CollapsibleItem
          v-for="(faq, i) in faqsConverted"
          v-slot="{ active, toggle, toggleInfoPage }"
          :key="i"
          :item-id="i"
        >
          <div class="faq-item">
            <div class="faq-item__trigger" @click="toggle(), toggleInfoPage()">
              <h4 class="faq-item__heading">{{ faq.Heading }}</h4>
              <i
                class="far"
                :class="active ? 'fa-minus' : 'fa-plus'"
                aria-hidden="true"
              ></i>
            </div>
            <div class="html-content" v-if="active">
              <div
                v-if="!promotedPage.page"
                v-html="faq.Tekst"
                class="faq-item__body html-content"
              ></div>
              <component
                v-if="promotedPage.page"
                :is="promotedPageComponent"
                :id="promotedPage.id"
                container="Info"
                class="faq-item__body"
              />
            </div>
          </div>
        </CollapsibleItem>
      </CollapsibleList>
    </div>
  </div>
</template>

<script>
import { mapActions, mapMutations, mapGetters } from 'vuex';
import Section from '@/components/shared/Section';
import CollapsibleList from '@/components/shared/CollapsibleList';
import CollapsibleItem from '@/components/shared/CollapsibleItem';
import Card from '@/components/shared/Card';
import Poll from '@/components/timeline/promoted/Poll';
import {
  listenClickInsideContainer,
  removeClickInsideContainerListener
} from '@/helpers/content-event-helpers';
import { getPusherInstance } from '@/utils';
import { openAgendaLink } from '@/helpers/agenda-link-helper';
import ApiClient from '@/client';

export default {
  components: {
    Section,
    CollapsibleList,
    CollapsibleItem,
    Card,
    Poll
  },
  created() {
    getPusherInstance({ isReady: true }).bind(`info-page-change`, () => {
      this.fetchItems();
    });
  },
  props: {
    faqs: [Array, String],
    aboutOnlineEvent: String,
    hasFullEventDetails: [Boolean, String],
    lobbyFaqEnabled: [Boolean, String],
    caption: { type: String, default: null },
    promotedPage: { id: String, page: String },
    promotedPageComponent: Object
  },
  data() {
    return {
      pollSelected: null,
      containerClickEvent: null
    };
  },
  mounted() {
    // `container` is a plain element ref (no `$el`).
    this.containerClickEvent = listenClickInsideContainer(
      this.$refs.container,
      this.onPromotedPage,
      null,
      this.openInternalLink
    );
    // Capture phase: runs before the shared listener so plain links typed
    // by the admin (e.g. "www.site.com") open the real site.
    this.$refs.container.addEventListener('click', this.fixExternalLink, true);
  },
  beforeDestroy() {
    if (this.$refs.container) {
      this.$refs.container.removeEventListener(
        'click',
        this.fixExternalLink,
        true
      );
    }
    removeClickInsideContainerListener(
      this.$refs.container,
      this.containerClickEvent
    );
  },
  computed: {
    ...mapGetters('Authentication', ['getUser']),
    // No caption passed → "Information"; an empty caption → "About the Event".
    title() {
      if (this.caption === null || this.caption === undefined) {
        return this.$t('Views.Lobby.Sections.Information.information');
      }
      return this.caption || this.$t('Views.Lobby.FaqSection.aboutTheEvent');
    },
    faqsConverted() {
      if (this.faqs instanceof Array) {
        return this.faqs;
      }

      return JSON.parse(this.faqs);
    }
  },
  methods: {
    ...mapActions('Info', ['setPromotedPage']),
    // Links in info-page text that have no scheme would resolve against
    // this site and land on the wrong page — make them absolute and open
    // them in a new tab.
    fixExternalLink(evt) {
      const t = evt.target && evt.target.closest ? evt.target : null;
      // Profile / sponsor buttons: handle them right here, before anything
      // else can swallow the click.
      const sponsorBtn = t && t.closest('[data-tappin-sponsor-id]');
      if (sponsorBtn) {
        const raw = sponsorBtn.getAttribute('data-tappin-sponsor-id') || '';
        const sid = raw.startsWith('internal/') ? raw.split('/')[2] : raw;
        if (sid) {
          evt.preventDefault();
          evt.stopPropagation();
          this.openInternalLink({ type: 'sponsor', id: sid });
          return;
        }
      }
      const profileBtn = t && t.closest('[data-tappin-userprofile]');
      if (profileBtn) {
        evt.preventDefault();
        evt.stopPropagation();
        this.openInternalLink({ type: 'userprofile' });
        return;
      }
      const a = t && t.closest('a[href]');
      if (!a) return;
      const internal = (a.getAttribute('href') || '').trim().split('/');
      if (internal[0] === 'internal') {
        const kind = String(internal[1] || '').toLowerCase();
        if (
          kind === 'userprofile' ||
          kind === 'allsponsors' ||
          kind === 'sponsors' ||
          (kind === 'sponsor' && internal[2])
        ) {
          evt.preventDefault();
          evt.stopPropagation();
          this.openInternalLink({ type: kind, id: internal[2] });
          return;
        }
      }
      const href = (a.getAttribute('href') || '').trim();
      if (!href || /^(internal\/|#|\/|[a-z][a-z0-9+.-]*:)/i.test(href)) {
        if (/^https?:/i.test(href) && !a.target) {
          a.target = '_blank';
          a.rel = 'noopener noreferrer';
        }
        return;
      }
      a.setAttribute('href', `https://${href}`);
      a.target = '_blank';
      a.rel = 'noopener noreferrer';
    },
    // Page links from the lobby text. An info-page link opens that item in
    // the list (setting it as the "promoted page" would blank the list, as
    // only polls/quizzes have a component to show); speaker / sponsor links
    // go to their page; polls and quizzes keep the shared behaviour.
    onPromotedPage(payload) {
      const { page, id } = payload || {};
      if (page === 'info') {
        const idx = this.faqsConverted.findIndex(
          (p) => String(p.PageID || p.Id || p.id || '') === String(id)
        );
        const list = this.$refs.faqList;
        if (idx >= 0 && list) {
          list.sharedState.activeItem = idx;
          this.$nextTick(() => {
            const el = this.$refs.container.querySelectorAll('.faq-item')[idx];
            if (el && el.scrollIntoView) {
              el.scrollIntoView({ behavior: 'smooth', block: 'center' });
            }
          });
        }
        return;
      }
      if (page === 'sponsor' && id) {
        return this.openInternalLink({ type: 'sponsor', id });
      }
      if (page === 'speaker') {
        return this.openInternalLink({ type: 'speakers' });
      }
      this.setPromotedPage(payload);
    },
    // Internal links (`internal/<type>/<id>`) from the lobby info text.
    openInternalLink({ type: rawType, id }) {
      const type = String(rawType || '').toLowerCase();
      if (type === 'allsponsors' || (type === 'sponsors' && !id)) {
        return this.$router
          .push({ name: 'program', query: { tab: 'sponsors' } })
          .catch(() => {});
      }
      if (['agenda', 'myagenda', 'agendasession'].includes(type)) {
        return openAgendaLink(
          type === 'myagenda'
            ? 'myAgenda'
            : type === 'agenda'
            ? 'agenda'
            : 'session',
          id
        );
      }
      // Poll / quiz links from the editor's Link list: show the poll in the
      // open info item, like a poll button does.
      if (type === 'codedpoll') {
        return this.setPromotedPage({ page: 'coded_polls', id });
      }
      if (type === 'codedquiz') {
        return this.setPromotedPage({ page: 'coded_quizzes', id });
      }
      if ((type === 'poll' || type === 'quiz') && id) {
        // Ask the server which kind it really is (poll / quiz / treasure hunt).
        return ApiClient.getPoll(id, false)
          .then((r) => {
            const t = r && r.data && r.data.pollType;
            const page = t === 2 ? 'quiz' : t === 3 ? 'treasure_hunt' : 'poll';
            this.setPromotedPage({ page, id: (r.data && r.data.id) || id });
          })
          .catch(() => this.setPromotedPage({ page: type, id }));
      }
      const go = (loc) => this.$router.push(loc).catch(() => {});
      if (type === 'sponsor' && id)
        return go({ name: 'sponsor', params: { sponsorId: id } });
      if (type === 'session' && id) {
        return go({ name: 'program', query: { sessionDetails: id } });
      }
      if (type === 'userprofile') return go({ name: 'profile' });
      if (['speakers', 'documents', 'participants'].includes(type)) {
        const tab = {
          speakers: 'speakers',
          documents: 'docs',
          participants: 'people'
        }[type];
        return go({ name: 'program', query: { tab } });
      }
    },
    ...mapMutations('Pages', ['setItems']),
    fetchItems() {
      // Stored unresolved: the Pages getter resolves merge tags against the current profile.
      ApiClient.getLivePages().then((r) => {
        this.setItems(r.data);
      });
    }
  }
};
</script>

<style scoped lang="scss">
// Unified vertical rhythm with the other lobby sections (Speakers,
// Join the Community, Sponsors) — 32 px top/bottom on desktop, 20 px
// on mobile. Keeps the gap between every section equal.
.about-section {
  padding-top: 32px;
  padding-bottom: 32px;

  @media (max-width: 640px) {
    padding-top: 20px;
    padding-bottom: 20px;
  }
}

.about-card {
  background: #ffffff;
  border-radius: 16px;
  padding: 24px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.06);
  border: 1px solid #e5e7eb;

  &__title {
    font-size: 20px;
    font-weight: 700;
    color: #111827;
    margin-bottom: 12px;
  }

  &__body {
    font-size: 14px;
    color: #374151;
    line-height: 1.7;
  }
}

.faq-item {
  background: #ffffff;
  border-radius: 12px;
  border: 1px solid #e5e7eb;
  margin-bottom: 8px;
  overflow: hidden;

  // Drop the trailing margin on the last FAQ so the section ends
  // flush against the next lobby section instead of leaving a gap.
  &:last-child {
    margin-bottom: 0;
  }

  &__trigger {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 16px 20px;
    cursor: pointer;
    gap: 12px;

    &:hover {
      background: #f9fafb;
    }
  }

  &__heading {
    font-size: 14px;
    font-weight: 600;
    color: #111827;
    margin: 0;
    flex: 1;
  }

  &__body {
    padding: 0 20px 16px;
    font-size: 14px;
    color: #374151;
    line-height: 1.7;
  }
}

::v-deep .html-content > p:has(iframe) {
  position: relative;
  padding-top: 56.25%;
}
::v-deep .html-content > p > iframe {
  position: absolute;
  width: 100%;
  top: 0;
  height: 100%;
  left: 0;
}
</style>
